<template>
  <v-container fluid class="eva-wrap" elevation="0">
    <validation-observer ref="form" v-slot="{ invalid }">
      <v-form @submit.prevent="submit">
        <v-progress-linear v-if="loading" indeterminate class="global-loader mb-4" />

        <fieldset :disabled="loading" class="eva-fieldset">
          <!-- Header premium -->
          <v-card class="eva-card" elevation="10">
            <div class="eva-hero primary">
              <div class="eva-hero__left">
                <div class="eva-hero__title">
                  <v-icon class="mr-2" color="white">mdi-clipboard-text</v-icon>
                  AVALIAÇÃO
                </div>
                <div class="eva-hero__subtitle">
                  Pontue de 1 – muito mau; 2 – mau; 3 – suficiente; 4 – bom; 5 – muito bom.
                  Se o item não se aplicar ao trabalho prestado, anote <b>NA</b> (não aplicável).
                </div>
              </div>

              <div class="eva-hero__right" v-if="method=='update'">
                <v-chip class="mr-2" color="white" outlined>
                  <v-icon left small>mdi-calendar</v-icon>
                  {{ fields.year || '—' }}
                </v-chip>
                <v-chip color="white" outlined>
                  <v-icon left small>mdi-truck</v-icon>
                  {{ fields.supplier_id || '—' }}
                </v-chip>
              </div>
            </div>

            <!-- Filtros -->
            <v-card-text class="pt-6">
              <v-row dense class="eva-filters">
                <v-col cols="12" md="2">
                  <validation-provider v-slot="{ errors }" vid="Ano" name="Ano" rules="required">
                    <v-autocomplete
                      label="Ano *"
                      :disabled="headerDisabled"
                      :items="years"
                      :error-messages="errors"
                      dense
                      outlined
                      class="eva-input"
                      v-model="fields.year"
                      @change="onYearChange"
                      prepend-inner-icon="mdi-calendar"
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="5">
                  <validation-provider v-slot="{ errors }" vid="Centro Custo" name="Centro Custo" rules="required">
                    <v-autocomplete
                      label="Centro Custo *"
                      :items="centros_custo"
                      :disabled="headerDisabled"
                      :error-messages="errors"
                      item-value="Id"
                      :item-text="item => item.Codigo + ' - ' + item.Descricao"
                      dense
                      outlined
                      class="eva-input"
                      v-model="fields.central_cost_id"
                      @change="onCentralCostChange"
                      prepend-inner-icon="mdi-office-building-marker"
                      :loading="loadingCentralCosts"
                      no-data-text="Sem centros de custo para este fornecedor/ano"
                    />
                  </validation-provider>
                </v-col>

                <v-col cols="12" md="5">
                  <validation-provider v-slot="{ errors }" vid="Fornecedor" name="Fornecedor" rules="required">
                    <v-autocomplete
                      label="Fornecedor *"
                      :items="suppliers"
                      :error-messages="errors"
                      item-value="Fornecedor"
                      :disabled="headerDisabled"
                      :item-text="item => item.Fornecedor + ' - ' + item.Nome"
                      dense
                      outlined
                      class="eva-input"
                      v-model="fields.supplier_id"
                      @change="onSupplierChange"
                      prepend-inner-icon="mdi-truck-delivery"
                    >
                      <!-- como aparece cada opção na lista -->
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                          v-bind="attrs"
                          v-on="on"
                          :class="{ 'eva-evaluated-item': !!item.evaluated }"
                        >
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ item.Fornecedor }} - {{ item.Nome }}
                            </v-list-item-title>
                          </v-list-item-content>

                          <v-list-item-action v-if="!!item.evaluated">
                            <v-chip x-small outlined color="primary">Já avaliado</v-chip>
                          </v-list-item-action>
                        </v-list-item>
                      </template>

                      <!-- como aparece o valor selecionado -->
                      <template v-slot:selection="{ item }">
                        <span :class="{ 'eva-evaluated-selection': !!item && !!item.evaluated }">
                          {{ item ? (item.Fornecedor + ' - ' + item.Nome) : '' }}
                        </span>
                      </template>
                    </v-autocomplete>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- Linha 1: Anual vs Extraordinária (exclusivo) -->
              <v-row dense class="eva-filters mt-2">
                <v-col cols="12" md="6">
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-checkbox
                        v-model="fields.is_annual"
                        label="Anual"
                        dense
                        :disabled="headerDisabled"
                        hide-details
                        class="mt-0"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-checkbox
                        v-model="fields.is_extraordinary"
                        label="Extraordinária"
                        :disabled="headerDisabled"
                        dense
                        hide-details
                        class="mt-0"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row dense class="eva-filters">
                <v-col cols="12">
                  <v-divider class="my-2" />
                </v-col>
              </v-row>

              <!-- Linha 2: Fornecimentos vs Subempreitadas (exclusivo) + dropdown na mesma linha -->
              <v-row dense class="eva-filters">
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="fields.is_supplies"
                    label="Fornecimentos"
                    dense
                    hide-details
                    :disabled="headerDisabled"
                    class="mt-0"
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="fields.is_subcontracts"
                    label="Subempreitadas"
                    :disabled="headerDisabled"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-if="typeDropdownVisible"
                    :items="typeDropdownItems"
                    :disabled="headerDisabled"
                    v-model="typeDropdownValue"
                    dense
                    outlined
                    class="eva-input"
                    :label="typeDropdownLabel"
                    :prepend-inner-icon="typeDropdownIcon"
                    clearable
                  />
                </v-col>
              </v-row>

            </v-card-text>
          </v-card>

          <!-- Conteúdo (tabela) -->
          <v-card v-if="showQuestionnaire" class="eva-card mt-6" elevation="10">
            <v-card-title class="eva-card__title">
              <v-icon class="mr-2" color="primary">mdi-star-check</v-icon>
              Itens de avaliação
              <v-spacer />
              <v-chip class="mr-2" color="primary" outlined>
                <v-icon left small>mdi-shield-star</v-icon>
                Escala 0–5
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-2">
              <v-simple-table dense class="eva-table">
                <thead>
                  <tr>
                    <th class="text-left">ITENS</th>
                    <th class="text-left eva-col-score">PONTUAÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="eva-item">Cumprimento dos prazos estabelecidos</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 1" name="Ponto 1" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.estabilished_deadlines"
                          dense
                          outlined
                          class="eva-input mt-2"
                          label="Pontuação"
                          :error-messages="errors"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-timer-sand"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <!-- P2: Equipamento (ESCONDE em Fornecimentos) -->
                  <tr v-if="!isSupplies">
                    <td class="eva-item">Equipamento</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 2" name="Ponto 2" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.equipment"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-excavator"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr v-if="!isSupplies">
                    <td class="eva-item">Meios humanos</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 3" name="Ponto 3" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.human_resources"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-account-group"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr>
                    <td class="eva-item">Relacionamento</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 4" name="Ponto 4" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.relationship"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-handshake"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr>
                    <td class="eva-item">
                      Qualidade dos trabalhos/fornecimentos, quando aplicável, das regras de segurança da informação.
                      <div class="eva-item__hint">
                        (Cumprimento das exigências estipuladas no Caderno de Encargos e Projeto, assim como regulamentação e disposições legais aplicáveis aos trabalhos adjudicados)
                      </div>
                    </td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 5" name="Ponto 5" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.quality_security"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-shield-check"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr v-if="!isSupplies">
                    <td class="eva-item">Cumprimento do horário de trabalho</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 6" name="Ponto 6" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.work_schedule"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-calendar-clock"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr v-if="!isSupplies">
                    <td class="eva-item">
                      Cumprimento do Plano de Segurança e Saúde da obra (PSS), incluindo entrega da lista de pessoal e documentação referida no contrato
                    </td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 7" name="Ponto 7" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.pss_compliance"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-hard-hat"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr v-if="!isSupplies">
                    <td class="eva-item">
                      Cumprimento do Plano de Gestão Ambiental da obra (PGAO), incluindo a correta triagem e armazenamento de resíduos, utilização racional de recursos 
                      <div class="eva-item__hint">(consumo de água, energia, materiais, etc.)</div>
                    </td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 8" name="Ponto 8" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.pgao_compliance"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-leaf"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr>
                    <td class="eva-item">Atendimento de reclamações</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 9" name="Ponto 9" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.complaints_attendance"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-headset"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr>
                    <td class="eva-item">Impressão geral</td>
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Ponto 10" name="Ponto 10" rules="required">
                        <v-select
                          :items="avaliacoes"
                          v-model="fields.general_impression"
                          dense
                          outlined
                          class="eva-input mt-2"
                          :error-messages="errors"
                          label="Pontuação"
                          item-value="value"
                          :item-text="item => item.value + ' - ' + item.id"
                          prepend-inner-icon="mdi-star"
                        />
                      </validation-provider>
                    </td>
                  </tr>

                  <tr>
                    <td class="eva-item">Observações</td>
                    <td>
                      <v-textarea
                        v-model="fields.obs"
                        dense
                        outlined
                        class="eva-input mt-2"
                        rows="3"
                        auto-grow
                        prepend-inner-icon="mdi-note-text"
                      />
                    </td>
                  </tr>

                  <tr v-if="method=='update'">
                    <td colspan="2" class="eva-meta">
                      <v-row dense>
                        <v-col cols="12" md="3" class="eva-meta__label">Responsável</v-col>
                        <v-col cols="12" md="3">{{ fields.user_creator && fields.user_creator.name ? fields.user_creator.name : '' }}</v-col>
                        <v-col cols="12" md="3" class="eva-meta__label">Data</v-col>
                        <v-col cols="12" md="3">{{fields.created_at}}</v-col>
                      </v-row>
                    </td>
                  </tr>

                  <tr v-if="method=='update'">
                    <td colspan="2" class="eva-meta">
                      <v-row dense>
                        <v-col cols="12" md="3" class="eva-meta__label">Resp. Serviço</v-col>
                        <v-col cols="12" md="3">{{ fields.user_responsible && fields.user_responsible.name ? fields.user_responsible.name : '' }}</v-col>
                        <v-col cols="12" md="3" class="eva-meta__label">Avaliação</v-col>
                        <v-col cols="12" md="3">{{fields.evaluation_ci}}</v-col>
                      </v-row>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table> 
            </v-card-text>

            <!-- Actions -->
            <v-card-actions class="eva-actions">
              <v-spacer />
              <v-btn :disabled="invalid" depressed color="primary" class="eva-save" type="submit">
                <v-icon left>mdi-content-save</v-icon>
                Gravar
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Se não houver conteúdo, dá um hint -->
          <v-card v-else class="eva-card mt-6" elevation="0" outlined>
            <v-card-text class="eva-empty">
              <v-icon class="mr-2" color="primary">mdi-filter</v-icon>
              Selecionar&nbsp;<span class="font-weight-bold">Ano</span>,&nbsp;<span class="font-weight-bold">Centro de Custo</span>&nbsp;e depois&nbsp;<span class="font-weight-bold">Fornecedor</span>&nbsp;para mostrar a avaliação.
            </v-card-text>
          </v-card>
        </fieldset>
      </v-form>
    </validation-observer>

    <ErrorSnackbar :message="error.message" v-model="error.show" />
    <DialogSuccess :opened="success" @on-ok="onSuccess">
      Avaliação gravada com sucesso
    </DialogSuccess>
  </v-container>
</template>

<script>
import Suppliers from '@/api/Suppliers.js'
import SupplierEvaluationAPI from '@/api/SupplierEvaluation.js'
import Works from '@/api/Works.js'
import DialogSuccess from '@/components/UI/DialogSuccess.vue';
import ErrorSnackbar from "@/components/UI/Snackbars/ErrorSnackbar";
import axios from "axios";

export default {
  name: "Evaluation",
  components: {
    DialogSuccess,
    ErrorSnackbar
  },
  props: {
    method: String,
    evaluation: Object
  },
  watch: {
    'fields.is_supplies'(val) {
      if (val) {
        this.fields.is_subcontracts = false;
        this.fields.subcontracts_type = null;

        // ✅ NOVO: esconder e forçar NA automaticamente
        this.forceNAForSupplies();
      } else {
        this.fields.supplies_type = null;
      }
    },
    // Anual vs Extraordinária (só uma pode estar ativa)
    'fields.is_annual'(val) {
      if (val) this.fields.is_extraordinary = false;
    },
    'fields.is_extraordinary'(val) {
      if (val) this.fields.is_annual = false;
    },

    // Fornecimentos vs Subempreitadas (só uma pode estar ativa)
    'fields.is_supplies'(val) {
      if (val) {
        this.fields.is_subcontracts = false;
        this.fields.subcontracts_type = null;
      } else {
        this.fields.supplies_type = null;
      }
    },
    'fields.is_subcontracts'(val) {
      if (val) {
        this.fields.is_supplies = false;
        this.fields.supplies_type = null;
      } else {
        this.fields.subcontracts_type = null;
      }
    },
    evaluation: {
      immediate: true,
      handler(val) {
        if (!val) return;

        // Mapeamento backend -> frontend (sem mudar o front)
        this.fields = {
          ...this.fields,

          id: val.id ?? null,

          estabilished_deadlines: val.established_deadlines !== undefined && val.established_deadlines !== null
            ? parseInt(val.established_deadlines, 10)
            : null,

          equipment: val.equipment !== undefined && val.equipment !== null
            ? parseInt(val.equipment, 10)
            : null,

          human_resources: val.human_resources !== undefined && val.human_resources !== null
            ? parseInt(val.human_resources, 10)
            : null,

          relationship: val.relationship !== undefined && val.relationship !== null
            ? parseInt(val.relationship, 10)
            : null,

          quality_security: val.quality_security !== undefined && val.quality_security !== null
            ? parseInt(val.quality_security, 10)
            : null,

          work_schedule: val.work_schedule !== undefined && val.work_schedule !== null
            ? parseInt(val.work_schedule, 10)
            : null,

          pss_compliance: val.pss_compliance !== undefined && val.pss_compliance !== null
            ? parseInt(val.pss_compliance, 10)
            : null,

          pgao_compliance: val.pgao_compliance !== undefined && val.pgao_compliance !== null
            ? parseInt(val.pgao_compliance, 10)
            : null,

          complaints_attendance: val.complaints_attendance !== undefined && val.complaints_attendance !== null
            ? parseInt(val.complaints_attendance, 10)
            : null,

          general_impression: val.general_impression !== undefined && val.general_impression !== null
            ? parseInt(val.general_impression, 10)
            : null,

          obs: val.obs ?? null,

          is_annual: !!val.is_annual,
          is_extraordinary: !!val.is_extraordinary,
          is_supplies: !!val.is_supplies,
          is_subcontracts: !!val.is_subcontracts,
          supplies_type: val.supplies_type ?? null,
          subcontracts_type: val.subcontracts_type ?? null,

          supplier_id: val.Fornecedor ?? val.supplier_id ?? null,

          year: val.year !== undefined && val.year !== null
            ? parseInt(val.year, 10)
            : null,
        };

        if (this.fields.is_supplies) {
          this.forceNAForSupplies();
        }

        this.fields.user_creator = val.user_creator;
        this.fields.user_responsible = val.user_responsible;
        this.fields.created_at = val.created_at;
        this.fields.central_cost_id = val.central_cost.Codigo;
        this.fields.evaluation_ci = val.evaluation_ci;

        // ✅ NO UPDATE: mostrar logo os itens
        if (this.method === 'update') {
          this.availabilityOk = true; // já é um registo existente
        }

        this.$nextTick(() => {
          this.loadCentralCosts(true); // true = modo update (não limpar central_cost_id)
        });       
      }
    }
  },
  data() {
    return {
      centros_custo: [],
      availabilityOk: false,
      loadContent: false,
      loading: true,
      loadingCentralCosts: false,
      suppliers: [],
      success: false,
      avaliacoes: [
        { id: 'NA', value: 0 },
        { id: 'Muito mau', value: 1 },
        { id: 'Mau', value: 2 },
        { id: 'Suficiente', value: 3 },
        { id: 'Bom', value: 4 },
        { id: 'Muito Bom', value: 5 },
      ],
      suppliesItems: [
        'Materiais de construção civil (cerâmicos, loiças, revestimentos, telhas, tijolos)',
        'Adjuvantes',
        'Argamassas',
        'EPI’s',
        'Madeira',
        'Geotêxtil',
        'Material pétreo',
        'Perfis e chapas metálicas',
        'Produtos petrolíferos',
        'Peças sobresselentes',
        'Tintas',
        'Aço',
        'Betão',
        'Software',
        'Cimento',
        'Mist. Betuminosas',
        'Agregados',
        'Pré-fabricados',
        'Calibrações',
        'Ensaios',
        'Tubagens',
        'Equipamentos',
        'Outros',
      ],
      subcontractsItems: [
        'SCIE',
        'Caixilharias',
        'Aluguer mão de obra',
        'Movimento de terras',
        'Prestação de serviços',
        'Aluguer de equipamentos',
        'AVAC e Eletricidade',
        'Construção Civil',
        'Betão armado',
        'Eletromecânica',
        'Estruturas metálicas',
        'Geotecnia',
        'Gestão Resíduos',
        'Guardas metálicas',
        'Montagem de andaimes',
        'Paisagismo',
        'Pavimentação',
        'Aparelhos de apoio',
        'Pré-esforço',
        'Juntas de dilatação',
        'Demolições',
        'Sinalização',
        'Informática',
        'Arqueologia',
        'Outros',
      ],
      fields: {
        id: null,

        // scores
        estabilished_deadlines: null, // mantém typo do front
        equipment: null,
        human_resources: null,
        relationship: null,
        quality_security: null,
        work_schedule: null,
        pss_compliance: null,
        pgao_compliance: null,
        complaints_attendance: null,
        general_impression: null,
        evaluation_ci: null,
        obs: null,

        // filtros
        supplier_id: null,
        central_cost_id: null,
        year: null,

        // header
        is_annual: false,
        is_extraordinary: false,
        is_supplies: false,
        is_subcontracts: false,
        supplies_type: null,
        subcontracts_type: null,
      },
      years: [],
      error: {
        show: false,
        message: null
      },
    }
  },
  computed: {
    suppliesItemsSorted() {
      return this.sortAZ(this.suppliesItems)
    },
    subcontractsItemsSorted() {
      return this.sortAZ(this.subcontractsItems)
    },

    typeDropdownItems() {
      return this.fields.is_supplies ? this.suppliesItemsSorted : this.subcontractsItemsSorted
    },
    isSupplies() {
      return !!this.fields.is_supplies;
    },
    headerDisabled() {
      return this.method === 'update';
    },
    isBaseComplete() {
      return !!this.fields.year && !!this.fields.central_cost_id && !!this.fields.supplier_id;
    },

    isHeaderComplete() {
      const hasAnnualType = (this.fields.is_annual || this.fields.is_extraordinary) &&
                            !(this.fields.is_annual && this.fields.is_extraordinary);

      const hasSupplyType = (this.fields.is_supplies || this.fields.is_subcontracts) &&
                            !(this.fields.is_supplies && this.fields.is_subcontracts);

      const hasDropdownValue =
        (this.fields.is_supplies && !!this.fields.supplies_type) ||
        (this.fields.is_subcontracts && !!this.fields.subcontracts_type);

      return hasAnnualType && hasSupplyType && hasDropdownValue;
    },

    showQuestionnaire() {
      return this.availabilityOk && this.isBaseComplete && this.isHeaderComplete;
    },
    typeDropdownVisible() {
      return !!this.fields.is_supplies || !!this.fields.is_subcontracts;
    },
    typeDropdownLabel() {
      return this.fields.is_supplies ? 'Tipo de fornecimentos' : 'Tipo de subempreitadas';
    },
    typeDropdownIcon() {
      return this.fields.is_supplies ? 'mdi-package-variant' : 'mdi-account-hard-hat';
    },
    typeDropdownValue: {
      get() {
        if (this.fields.is_supplies) return this.fields.supplies_type;
        if (this.fields.is_subcontracts) return this.fields.subcontracts_type;
        return null;
      },
      set(v) {
        if (this.fields.is_supplies) this.fields.supplies_type = v;
        if (this.fields.is_subcontracts) this.fields.subcontracts_type = v;
      }
    },
    canPickCentralCost() {
      return !!this.fields.supplier_id && !!this.fields.year;
    }
  },
  mounted() {
    if (this.method == 'create')
      this.fields.supplier_id = this.$route.params.supplier_id

    // ano por defeito = ano atual
    if (!this.fields.year) {
      this.fields.year = new Date().getFullYear();
    }

    this.fillBaseData();
  },
  methods: {
    sortAZ(list) {
      if (!Array.isArray(list)) return []
      return list
        .slice()
        .sort((a, b) =>
          String(a).localeCompare(String(b), 'pt', { sensitivity: 'base' })
        )
    },
    forceNAForSupplies() {
      // NA = 0 no teu array de avaliações
      this.fields.equipment = 0;
      this.fields.human_resources = 0;
      this.fields.work_schedule = 0;
      this.fields.pss_compliance = 0;
      this.fields.pgao_compliance = 0;
    },
    // 1) Mudança de fornecedor: limpa CC, limpa conteúdo, e só carrega CC se já houver ano
    onSupplierChange() {
      this.availabilityOk = false;

      if (!this.fields.supplier_id || !this.fields.year || !this.fields.central_cost_id) return;

      this.checkAvailability();
    },

    // 2) Mudança de ano: limpa CC, limpa conteúdo, e só carrega CC se já houver fornecedor
    onYearChange() {
      this.availabilityOk = false;
      this.resetAfterYearOrCentralCostChange();
      this.loadCentralCosts();
    },
    loadCentralCosts(isUpdate = false) {
      this.loadingCentralCosts = true;

      let show_all = false
      if(this.method == 'update'){
        if(this.fields.user_creator.id != this.$store.getters.id)
          if(this.$store.getters.hasPermission(['super', 'supplier_evaluations.read_always_all']))
            show_all = true
      }

      SupplierEvaluationAPI.getCentralCosts({ year: this.fields.year, show_all: show_all })
        .then((resp) => {
          this.centros_custo = resp.data || [];
          
          // ✅ UPDATE: se já vem central_cost_id, garantir que existe nos items
          if (isUpdate && this.fields.central_cost_id) {
            const exists = this.centros_custo.some(cc => cc.Codigo === this.fields.central_cost_id);

            // se existir, força carregar fornecedores logo (sem limpar)
            if (exists) {
              const cc = this.centros_custo.find(cc => cc.Codigo === this.fields.central_cost_id);
              this.fields.central_cost_id = cc.Id;
              this.fetchSuppliersByCentralCost();
              return;
            }

            // se não existir, limpa porque é inválido para o ano
            this.fields.central_cost_id = null;
            this.suppliers = [];
            this.fields.supplier_id = null;

            if(this.method != 'update')
              this.loadContent = false;
            return;
          }

          // ✅ CREATE: se só houver 1 centro de custo -> auto seleciona
          if (!isUpdate && this.centros_custo.length === 1) {
            this.fields.central_cost_id = this.centros_custo[0].Id;
            this.onCentralCostChange();
          }
        })
        .catch(() => {
          this.centros_custo = [];
          this.error.message = "Erro ao carregar centros de custo.";
          this.error.show = true;
        })
        .finally(() => {
          this.loadingCentralCosts = false;
        });
    },

    fetchSuppliersByCentralCost() {
      this.loading = true;

      let show_all = false
      if(this.method == 'update'){
        if(this.fields.user_creator.id != this.$store.getters.id)
          if(this.$store.getters.hasPermission(['super', 'supplier_evaluations.read_always_all']))
            show_all = true
      }

      SupplierEvaluationAPI.getSupplierCentralCosts({
        year: this.fields.year,
        central_cost_id: this.fields.central_cost_id,
        show_all: show_all
      })
        .then((resp) => {
          const data = resp.data || [];

          this.suppliers = data.map(s => ({
            ...s,
            evaluated: String(s.evaluated) === '1' // fica boolean true/false
          }));

          // opcional: se só houver 1 fornecedor, seleciona automaticamente e verifica disponibilidade
          if (this.suppliers.length === 1) {
            this.fields.supplier_id = this.suppliers[0].Fornecedor;
            this.onSupplierChange();
          }
        })
        .catch(() => {
          this.suppliers = [];
          this.error.message = "Erro ao carregar fornecedores para este centro de custo.";
          this.error.show = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 3) Mudança de Centro de Custo: só aqui é que mostramos a avaliação (via checkAvailability)
    onCentralCostChange() {
      this.availabilityOk = false;
      this.fields.supplier_id = null;
      this.suppliers = [];
      // já não precisas do loadContent aqui
      if (!this.fields.year || !this.fields.central_cost_id) return;

      this.fetchSuppliersByCentralCost();
    },

    resetAfterYearOrCentralCostChange() {
      this.fields.central_cost_id = null;
      this.fields.supplier_id = null;
      this.centros_custo = [];
      this.suppliers = [];
      this.loadContent = false;
    },
    checkAvailability() {
      const data = {
        ano: this.fields.year,
        supplier_id: this.fields.supplier_id,
        central_cost_id: this.fields.central_cost_id,
      };

      SupplierEvaluationAPI.checkAvailability(data).then((resp) => {
        if (resp.data.available === true) {
          this.availabilityOk = true;
        } else {
          this.availabilityOk = false;
          this.error.message = "Já existe uma avaliação para este fornecedor, centro de custo e ano.";
          this.error.show = true;
        }
      });
    },

    onSuccess() {
      this.$router.push('/evaluations')
    },

    getYears() {
      const currentYear = new Date().getFullYear() - 1;
      const startYear = 2025;
      const years = [];
      for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
      }

      if(years.length === 1) {
        this.fields.year = years[0];
      }
      return years;
    },

    fillBaseData() {
      this.years = this.getYears();

      // se não vier year preenchido (create), já fica o atual
      if (!this.fields.year) {
        this.fields.year = new Date().getFullYear();
      }

      this.loadCentralCosts();

      this.loading = false;
    },


    submit() {
      this.$refs.form.validate().then((result) => {
        this.loading = true;

        if (!result) {
          this.loading = false;
          return;
        }

        // ✅ garante NA antes de enviar
        if (this.fields.is_supplies) {
          this.forceNAForSupplies();
        }

        SupplierEvaluationAPI[this.method](this.fields, this.fields.id)
          .then(() => {
            this.success = true;
            this.loading = false;
          })
          .catch(err => {
            this.loading = false;
            if (err.response.status == 422) {
              this.$refs.form.setErrors(err.response.data.errors);
              return;
            }
            this.error.title = "Erro " + err.response.status;
            this.error.message = err.response.data.message;
          });
      })
    }
  }
}
</script>

<style>
.eva-wrap {
  max-width: 1200px;
}

.eva-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
}

.eva-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.eva-hero {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px;
  color: #fff;
}

.eva-hero__title {
  display: flex;
  align-items: center;
  font-weight: 800;
  letter-spacing: 0.4px;
  font-size: 16px;
}

.eva-hero__subtitle {
  margin-top: 6px;
  opacity: 0.92;
  font-size: 13px;
  line-height: 1.35;
  max-width: 860px;
}

.eva-hero__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.eva-filters {
  margin-top: 4px;
}

.eva-input :deep(.v-input__slot) {
  border-radius: 12px !important;
  background: linear-gradient(180deg, rgba(250, 250, 252, 1) 0%, rgba(245, 246, 250, 1) 100%);
}

.eva-table thead th {
  font-weight: 800;
  letter-spacing: .2px;
  color: rgba(0,0,0,0.75);
}

.eva-table tbody tr:hover {
  background: rgba(90, 103, 165, 0.06);
}

.eva-col-score {
  width: 320px;
}

.eva-item {
  font-weight: 600;
  color: rgba(0,0,0,0.82);
}

.eva-item__hint {
  margin-top: 6px;
  font-weight: 400;
  font-size: 12px;
  color: rgba(0,0,0,0.6);
}

.eva-card__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-meta {
  background: rgba(0,0,0,0.02);
}

.eva-meta__label {
  font-weight: 700;
  color: rgba(0,0,0,0.7);
}

.eva-actions {
  padding: 16px 20px 18px;
}

.eva-save {
  border-radius: 12px;
  min-width: 160px;
}

.eva-empty {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0,0,0,0.65);
}
.eva-evaluated-item {
  background: rgba(76, 175, 80, 0.12); /* verde suave */
}

.eva-evaluated-item:hover {
  background: rgba(76, 175, 80, 0.18);
}

.eva-evaluated-selection {
  background: rgba(76, 175, 80, 0.15);
  padding: 2px 6px;
  border-radius: 6px;
}
</style>
